import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormHelperText,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Slider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Save as SaveIcon,
  Add as AddIcon,
  LocalOffer as TagIcon,
} from "@mui/icons-material";
import ItemRenderer from "../items/ItemRenderer";
import ItemTypeGallery from "../items/ItemTypeGallery";
import { createTemplateItem, ITEM_TYPES } from "../../constants/itemTypes";
import {
  createTemplate,
  validateTemplate,
  updateTemplate,
  getTemplateCategories,
} from "../../models/Template";

/**
 * Enhanced template form component for managers with advanced features
 *
 * @param {Object} props
 * @param {Object} props.template - Template data (null for new template)
 * @param {Function} props.onSave - Callback when form is submitted
 * @param {Function} props.onCancel - Callback when form is cancelled
 */
const TemplateForm = ({ template = null, onSave, onCancel }) => {
  const isEditing = !!template;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Form state using new Template model structure
  const [formData, setFormData] = useState({
    name: template?.name || "",
    description: template?.description || "",
    version: template?.version || 1,
    isActive: template?.isActive !== undefined ? template.isActive : true,
    items: template?.items || [],
    metadata: {
      category: template?.metadata?.category || "general",
      country: template?.metadata?.country || "Italy",
      tags: template?.metadata?.tags || [],
    },
  });

  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState("");

  useEffect(() => {
    setFormData({
      name: template?.name || "",
      description: template?.description || "",
      version: template?.version || 1,
      isActive: template?.isActive !== undefined ? template.isActive : true,
      items: template?.items || [],
      metadata: {
        category: template?.metadata?.category || "general",
        country: template?.metadata?.country || "Italy",
        tags: template?.metadata?.tags || [],
      },
    });
  }, [template]);

  // Available categories for templates
  const categories = [
    { value: "general", label: "General" },
    { value: "safety", label: "Safety" },
    { value: "maintenance", label: "Maintenance" },
    { value: "inspection", label: "Inspection" },
    { value: "quality", label: "Quality Assurance" },
    { value: "compliance", label: "Compliance" },
    { value: "training", label: "Training" },
    { value: "audit", label: "Audit" },
  ];

  const countries = [
    { value: "Denmark", label: "Denmark" },
    { value: "Italy", label: "Italy" },
  ];

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      // Handle nested fields like metadata.category
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.metadata.tags.includes(newTag.trim())) {
      handleInputChange("metadata.tags", [
        ...formData.metadata.tags,
        newTag.trim(),
      ]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleInputChange(
      "metadata.tags",
      formData.metadata.tags.filter((tag) => tag !== tagToRemove)
    );
  };

  const handleAddItem = (itemType, configuration = {}) => {
    const itemOptions = {
      label: `New ${itemType} item`,
      order: formData.items.length,
    };
    console.log(itemType, configuration);
    // Handle configuration data for specific item types
    if (itemType === ITEM_TYPES.SELECT && configuration) {
      console.log(configuration.options);
      itemOptions.config = {
        optionList: configuration,
      };
    }

    if (itemType === ITEM_TYPES.NUMBER && configuration) {
      console.log(configuration.options);
      itemOptions.config = {
        options: configuration,
      };
    }

    const newItem = createTemplateItem(itemType, itemOptions);

    handleInputChange("items", [...formData.items, newItem]);
  };

  const handleUpdateItem = (itemId, updates) => {
    handleInputChange(
      "items",
      formData.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      )
    );
  };

  const handleDeleteItem = (itemId) => {
    handleInputChange(
      "items",
      formData.items.filter((item) => item.id !== itemId)
    );
  };

  const handleReorderItems = (dragIndex, dropIndex) => {
    const items = [...formData.items];
    const draggedItem = items[dragIndex];

    items.splice(dragIndex, 1);
    items.splice(dropIndex, 0, draggedItem);

    // Update order property
    const reorderedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    handleInputChange("items", reorderedItems);
  };

  const validateForm = () => {
    const validation = validateTemplate(formData);

    if (!validation.isValid) {
      const newErrors = {};
      validation.errors.forEach((error) => {
        if (error.includes("name")) newErrors.name = error;
        else if (error.includes("description")) newErrors.description = error;
        else if (error.includes("version")) newErrors.version = error;
        else if (error.includes("items")) newErrors.items = error;
      });
      setErrors(newErrors);
    }

    return validation;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log(e);

    const validation = validateForm();
    if (validation.isValid) {
      let templateData;

      if (isEditing) {
        templateData = updateTemplate(template, formData);
      } else {
        templateData = createTemplate({
          ...formData,
          createdBy: "current_user_id", // This would come from auth context
        });
      }

      onSave(templateData);
    }
  };

  const isFormValid = formData.name.trim() && formData.items.length > 0;

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          {isEditing ? "Edit Template" : "Create New Template"}
        </Typography>

        {/* Basic Information */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              label="Template Name"
              variant="outlined"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
              margin="normal"
              error={!!errors.name}
              helperText={errors.name}
              // inputProps={{ maxLength: 100 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Version"
              variant="outlined"
              value={formData.version}
              onChange={(e) => handleInputChange("version", e.target.value)}
              margin="normal"
              error={!!errors.version}
              helperText={
                errors.version || "Use integer versioning (default: 1)"
              }
              disabled // Only allow version editing for existing templates
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label="Description"
          variant="outlined"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          multiline
          rows={3}
          margin="normal"
          error={!!errors.description}
          helperText={errors.description}
          inputProps={{ maxLength: 500 }}
        />

        {/* Metadata Section */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          Template Configuration
        </Typography>

        <Grid container spacing={2}>
          {/* <Grid size={{ xs: 12, sm: 12, md: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                value={formData.metadata.category}
                onChange={(e) =>
                  handleInputChange("metadata.category", e.target.value)
                }
                label="Category"
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid> */}
          <Grid size={{ xs: 12, sm: 12, md: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="country-label">Country</InputLabel>
              <Select
                labelId="country-label"
                value={formData.metadata.country}
                onChange={(e) =>
                  handleInputChange("metadata.country", e.target.value)
                }
                label="Country"
              >
                {countries.map((country) => (
                  <MenuItem key={country.value} value={country.value}>
                    {country.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Tags Section */}
        {/* <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Tags
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {formData.metadata.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              label="Add tag"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              onClick={handleAddTag}
              startIcon={<TagIcon />}
              disabled={!newTag.trim()}
            >
              Add
            </Button>
          </Box>
        </Box> */}

        {/* Template Status */}
        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={(e) => handleInputChange("isActive", e.target.checked)}
            />
          }
          label="Template is active and available for use"
          sx={{ mt: 2 }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Template Items Section */}
        <Typography variant="h6" gutterBottom>
          Template Items ({formData.items.length})
        </Typography>

        {errors.items && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.items}
          </Alert>
        )}

        {formData.items.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            {formData.items
              .sort((a, b) => a.order - b.order)
              .map((item, index) => (
                <ItemRenderer
                  key={item.id}
                  item={item}
                  index={index}
                  onUpdate={handleUpdateItem}
                  onDelete={handleDeleteItem}
                  draggable={true}
                  editable={true}
                  onReorder={handleReorderItems}
                  isTemplateView={true}
                />
              ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontStyle: "italic" }}
          >
            No items added yet. Add your first item below.
          </Typography>
        )}

        <ItemTypeGallery onAddItem={handleAddItem} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 3,
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Button variant="outlined" onClick={onCancel} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!isFormValid}
            fullWidth={isMobile}
          >
            {isEditing ? "Save Changes" : "Create Template"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default TemplateForm;
